import { TABLE_ACTION } from "./Constants.tsx";
import { ColumnHeaderType } from "./Types.tsx";
import {
  arrayToString,
  blankValueGetter,
  numberCurrencyFormatInTable,
  numberFormat,
  numberFormatWithoutCurrency,
  numberWithoutCurrencyFormatInTable,
  selectedFilterClass,
  stateNameGetter,
  valueWithDecimalPlace,
  numberFormatWithCurrency,
  pospValueGetter,
} from "./Util.tsx";

export const STATE_VIEW_HEADERS: ColumnHeaderType[] = [
  {
    name: "State",
    field: "state",
    showFilter: true,
    valueGetter: blankValueGetter,
    clickable: false,
    sortable: true,
  },
  {
    name: "Premium",
    field: "premium",
    showFilter: false,
    valueGetter: (row) => {
      const raw = Number(row?.amount);
      if (isNaN(raw)) return "₹0"; // fallback
      return "₹" + raw.toLocaleString("en-IN", { minimumFractionDigits: 2 });
    },
    clickable: false,
    classNameGetter: selectedFilterClass,
    sortable: true,
  },
  {
    name: "Sum Insured",
    field: "suminsured",
    showFilter: false,
    valueGetter: numberCurrencyFormatInTable,
    clickable: false,
    classNameGetter: selectedFilterClass,
    sortable: true,
  },
  {
    name: "Policies",
    field: "policies",
    showFilter: false,
    valueGetter: numberFormat,
    clickable: false,
    classNameGetter: selectedFilterClass,
    sortable: true,
  },
];

export const GRID_VIEW_HEADERS: ColumnHeaderType[] = [
  {
    name: "Grid Id",
    field: "gridid",
    showFilter: true,
    valueGetter: blankValueGetter,
    clickable: true,
    action: TABLE_ACTION.GRID_SELECTION,
    sortable: true,
  },
  {
    name: "Premium",
    field: "premium",
    showFilter: false,
    valueGetter: numberCurrencyFormatInTable,
    clickable: false,
    classNameGetter: selectedFilterClass,
    sortable: true,
  },
  {
    name: "Sum Insured",
    field: "suminsured",
    showFilter: false,
    valueGetter: numberCurrencyFormatInTable,
    clickable: false,
    classNameGetter: selectedFilterClass,
    sortable: true,
  },
  {
    name: "Policies",
    field: "policies",
    showFilter: false,
    valueGetter: numberFormat,
    clickable: false,
    classNameGetter: selectedFilterClass,
    sortable: true,
  },
];
export const POSP_HEADERS: ColumnHeaderType[] = [
  {
    name: "Grid Id",
    field: "gridid",
    showFilter: false,
    valueGetter: (row) => blankValueGetter(row, "gridid"),
    clickable: true,
    action: TABLE_ACTION.GRID_SELECTION,
    sortable: true,
  },
  {
    name: "First Name",
    field: "first_name",
    showFilter: false,
    valueGetter: pospValueGetter("first_name"),
    clickable: false,
    sortable: true,
  },
  {
    name: "Last Name",
    field: "last_name",
    showFilter: false,
    valueGetter: pospValueGetter("last_name"),
    clickable: false,
    sortable: true,
  },
];

export const POSP_ARABLE_HEADERS: ColumnHeaderType[] = [
  {
    name: "Grid Id",
    field: "gridid",
    showFilter: false,
    valueGetter: blankValueGetter,
    clickable: true,
    action: TABLE_ACTION.GRID_SELECTION,
    sortable: true,
  },
  {
    name: "Arable Area(acres)",
    field: "arable_area",
    showFilter: false,
    valueGetter: valueWithDecimalPlace,
    clickable: false,
    sortable: true,
  },
  {
    name: "Catchment Ratio",
    field: "catchment_ratio",
    showFilter: false,
    valueGetter: valueWithDecimalPlace,
    clickable: false,
    sortable: true,
  },
];

export const POSP_TARGET_HEADERS: ColumnHeaderType[] = [
  {
    name: "Grid Id",
    field: "gridid",
    showFilter: false,
    valueGetter: blankValueGetter,
    clickable: true,
    action: TABLE_ACTION.GRID_SELECTION,
    sortable: true,
  },
  {
    name: "Candidate Id",
    field: "candidate_id",
    showFilter: false,
    valueGetter: blankValueGetter,
    clickable: false,
    sortable: true,
  },
  {
    name: "Target value",
    field: "target_value",
    showFilter: false,
    valueGetter: numberCurrencyFormatInTable,
    clickable: false,
    sortable: true,
  },
  {
    name: "Achieved Percentage",
    field: "achieved_percentage",
    showFilter: false,
    valueGetter: valueWithDecimalPlace,
    clickable: false,
    sortable: true,
  },
];

export const POSP_LOCATION: ColumnHeaderType[] = [
  {
    name: "Grid Id",
    field: "gridid",
    showFilter: false,
    valueGetter: blankValueGetter,
    clickable: true,
    action: TABLE_ACTION.GRID_SELECTION,
    sortable: true,
  },
  {
    name: "Employee Id",
    field: "employeeuniqueid",
    showFilter: false,
    valueGetter: blankValueGetter,
    clickable: false,
    sortable: true,
  },
  {
    name: "Name",
    field: "employeename",
    showFilter: false,
    valueGetter: blankValueGetter,
    clickable: false,
    sortable: true,
  },
  {
    name: "Role",
    field: "rolename",
    showFilter: false,
    valueGetter: blankValueGetter,
    clickable: false,
    sortable: true,
  },
  {
    name: "Mobile Number",
    field: "mobile_number",
    showFilter: false,
    valueGetter: blankValueGetter,
    clickable: false,
    sortable: false,
  },
];

export const UNIVERSITIES_HEADERS: ColumnHeaderType[] = [
  {
    name: "Grid Id",
    field: "gridid",
    showFilter: false,
    valueGetter: blankValueGetter,
    clickable: true,
    action: TABLE_ACTION.GRID_SELECTION,
    sortable: true,
  },
  {
    name: "University Name",
    field: "college_name",
    showFilter: false,
    valueGetter: blankValueGetter,
    clickable: false,
    sortable: true,
  },
  {
    name: "Address",
    field: "address",
    showFilter: false,
    valueGetter: blankValueGetter,
    clickable: false,
    sortable: true,
  },
];

export const getCombinedBusinessHeaders = (
  item: string,
): ColumnHeaderType[] => {
  return [
    {
      name:
        item === "POSP"
          ? "POSP"
          : item === "grid"
            ? "Grid Id"
            : item === "state"
              ? "State"
              : "District",
      field:
        item === "POSP"
          ? "proposer_name"
          : item === "grid"
            ? "gridid"
            : item === "state"
              ? "vstate"
              : "vdistrict",
      showFilter: false,
      valueGetter: blankValueGetter,
      clickable: item !== "grid" && item !== "POSP",
      action: TABLE_ACTION.GRID_SELECTION,
      sortable: false,
    },
    {
      name: "Total Policy",
      field: "total_policies",
      showFilter: false,
      valueGetter: blankValueGetter,
      clickable: false,
      action: TABLE_ACTION.GRID_SELECTION,
      sortable: true,
    },
    {
      name: "Total Premium(₹)",
      field: "total_premium",
      showFilter: false,
      valueGetter: numberWithoutCurrencyFormatInTable,
      clickable: false,
      action: TABLE_ACTION.GRID_SELECTION,
      sortable: true,
    },
    {
      name: "Total Sum Insured(₹)",
      field: "total_suminsured",
      showFilter: false,
      valueGetter: numberWithoutCurrencyFormatInTable,
      clickable: false,
      action: TABLE_ACTION.GRID_SELECTION,
      sortable: true,
    },
    {
      name: "Accepted Policies",
      field: "accepted_policies",
      showFilter: false,
      valueGetter: blankValueGetter,
      clickable: false,
      action: TABLE_ACTION.GRID_SELECTION,
      sortable: true,
    },
    {
      name: "Accepted Premium(₹)",
      field: "accepted_premium",
      showFilter: false,
      valueGetter: numberWithoutCurrencyFormatInTable,
      clickable: false,
      action: TABLE_ACTION.GRID_SELECTION,
      sortable: true,
    },
    {
      name: "Accepted Sum Insured(₹)",
      field: "accepted_suminsured",
      showFilter: false,
      valueGetter: numberWithoutCurrencyFormatInTable,
      clickable: false,
      action: TABLE_ACTION.GRID_SELECTION,
      sortable: true,
    },
    {
      name: "Claim Intimated(₹)",
      field: "total_claims",
      showFilter: false,
      valueGetter: numberWithoutCurrencyFormatInTable,
      clickable: false,
      action: TABLE_ACTION.GRID_SELECTION,
      sortable: true,
    },
    {
      name: "Claim Calculated(₹)",
      field: "claim_calculated",
      showFilter: false,
      valueGetter: numberWithoutCurrencyFormatInTable,
      clickable: false,
      action: TABLE_ACTION.GRID_SELECTION,
      sortable: true,
    },
    {
      name: "Claim Paid(₹)",
      field: "total_claim_amount",
      showFilter: false,
      valueGetter: numberWithoutCurrencyFormatInTable,
      clickable: false,
      action: TABLE_ACTION.GRID_SELECTION,
      sortable: true,
    },
    {
      name: "Loss Ratio(%)",
      field: "loss_ratio",
      showFilter: false,
      valueGetter: blankValueGetter,
      clickable: false,
      action: TABLE_ACTION.GRID_SELECTION,
      sortable: true,
    },
    {
      name: "Previous Season Loss Ratio(%)",
      field: "previous_season_loss_ratio",
      showFilter: false,
      valueGetter: blankValueGetter,
      clickable: false,
      action: TABLE_ACTION.GRID_SELECTION,
      sortable: true,
    },
  ];
};

export const POLICIES_HEADERS: ColumnHeaderType[] = [
  {
    name: "Grid Id",
    field: "gridid",
    showFilter: false,
    valueGetter: blankValueGetter,
    clickable: true,
    action: TABLE_ACTION.GRID_SELECTION,
    sortable: true,
  },
  {
    name: "Policy Number",
    field: "policy_numbers",
    showFilter: false,
    valueGetter: arrayToString,
    clickable: false,
    sortable: false,
  },
  {
    name: "Policy Count",
    field: "policy_count",
    showFilter: false,
    valueGetter: numberFormat,
    clickable: false,
    sortable: true,
  },
  {
    name: "Crop Name",
    field: "crop_names",
    showFilter: false,
    valueGetter: arrayToString,
    clickable: false,
    sortable: false,
  },
  {
    name: "Cultivator Name",
    field: "cultivator_names",
    showFilter: false,
    valueGetter: arrayToString,
    clickable: false,
    sortable: false,
  },
  {
    name: "Area(acres)",
    field: "area",
    showFilter: false,
    valueGetter: valueWithDecimalPlace,
    clickable: false,
    sortable: true,
  },
];

export const CROPS_HEADERS: ColumnHeaderType[] = [
  {
    name: "Grid Id",
    field: "gridid",
    showFilter: false,
    valueGetter: blankValueGetter,
    clickable: true,
    action: TABLE_ACTION.GRID_SELECTION,
    sortable: true,
  },
  {
    name: "Crop Name",
    field: "crop_name",
    showFilter: false,
    valueGetter: blankValueGetter,
    clickable: false,
    sortable: true,
  },
  {
    name: "Premium",
    field: "premium",
    showFilter: false,
    valueGetter: blankValueGetter,
    clickable: false,
    sortable: true,
  },
  {
    name: "Sum Insured",
    field: "sum_insured",
    showFilter: false,
    valueGetter: blankValueGetter,
    clickable: false,
    sortable: true,
  },
  {
    name: "Area(acres)",
    field: "area",
    showFilter: false,
    valueGetter: valueWithDecimalPlace,
    clickable: false,
    sortable: true,
  },
];

export const CUSTOMERS_HEADERS: ColumnHeaderType[] = [
  {
    name: "Grid Id",
    field: "gridid",
    showFilter: false,
    valueGetter: blankValueGetter,
    clickable: true,
    action: TABLE_ACTION.GRID_SELECTION,
    sortable: true,
  },
  {
    name: "Customer Name",
    field: "cultivator_name",
    showFilter: false,
    valueGetter: blankValueGetter,
    clickable: false,
    sortable: true,
  },
  {
    name: "Crop Name",
    field: "crop_name",
    showFilter: false,
    valueGetter: blankValueGetter,
    clickable: false,
    sortable: true,
  },
];

export const PREMIUM_HEADERS: ColumnHeaderType[] = [
  {
    name: "Grid Id",
    field: "gridid",
    showFilter: false,
    valueGetter: blankValueGetter,
    clickable: true,
    action: TABLE_ACTION.GRID_SELECTION,
    sortable: true,
  },
  {
    name: "Premium",
    field: "premium",
    showFilter: false,
    valueGetter: numberCurrencyFormatInTable,
    clickable: false,
    sortable: true,
  },
  {
    name: "Premium Without GST",
    field: "premium_without_gst",
    showFilter: false,
    valueGetter: numberCurrencyFormatInTable,
    clickable: false,
    sortable: true,
  },
  {
    name: "Area(acres)",
    field: "area",
    showFilter: false,
    valueGetter: valueWithDecimalPlace,
    clickable: false,
    sortable: true,
  },
];

export const SUM_INSURED_HEADERS: ColumnHeaderType[] = [
  {
    name: "Grid Id",
    field: "gridid",
    showFilter: false,
    valueGetter: blankValueGetter,
    clickable: true,
    action: TABLE_ACTION.GRID_SELECTION,
    sortable: true,
  },
  {
    name: "Sum Insured",
    field: "suminsured",
    showFilter: false,
    valueGetter: numberCurrencyFormatInTable,
    clickable: false,
    sortable: true,
  },
  {
    name: "Area(acres)",
    field: "area",
    showFilter: false,
    valueGetter: valueWithDecimalPlace,
    clickable: false,
    sortable: true,
  },
];

export const AREA_HEADERS: ColumnHeaderType[] = [
  {
    name: "Grid Id",
    field: "gridid",
    showFilter: false,
    valueGetter: blankValueGetter,
    clickable: true,
    action: TABLE_ACTION.GRID_SELECTION,
    sortable: true,
  },
  {
    name: "Area(acres)",
    field: "area",
    showFilter: false,
    valueGetter: valueWithDecimalPlace,
    clickable: false,
    sortable: true,
  },
  {
    name: "Sum Insured",
    field: "suminsured",
    showFilter: false,
    valueGetter: numberCurrencyFormatInTable,
    clickable: false,
    sortable: true,
  },
  {
    name: "Premium",
    field: "premium",
    showFilter: false,
    valueGetter: blankValueGetter,
    clickable: false,
    sortable: true,
  },
];

export const CUSTOMER_COUNT_HEADERS: ColumnHeaderType[] = [
  {
    name: "Grid Id",
    field: "gridid",
    showFilter: false,
    valueGetter: blankValueGetter,
    clickable: true,
    action: TABLE_ACTION.GRID_SELECTION,
    sortable: true,
  },
  {
    name: "Area(acres)",
    field: "area",
    showFilter: false,
    valueGetter: valueWithDecimalPlace,
    clickable: false,
    sortable: true,
  },
  {
    name: "Customer",
    field: "customer_count",
    showFilter: false,
    valueGetter: blankValueGetter,
    clickable: false,
    sortable: true,
  },
  {
    name: "Cultivator Names",
    field: "cultivator_names",
    showFilter: false,
    valueGetter: arrayToString,
    clickable: false,
    sortable: false,
  },
  {
    name: "Policy Numbers",
    field: "policy_numbers",
    showFilter: false,
    valueGetter: arrayToString,
    clickable: false,
    sortable: false,
  },
];

export const CLAIMS_CUSTOMER_COUNT_HEADERS: ColumnHeaderType[] = [
  {
    name: "Grid Id",
    field: "gridid",
    showFilter: false,
    valueGetter: blankValueGetter,
    clickable: true,
    action: TABLE_ACTION.GRID_SELECTION,
    sortable: true,
  },
];

export const CLAIMS_CLAIM_PROCESSED_HEADERS: ColumnHeaderType[] = [
  {
    name: "Grid Id",
    field: "gridid",
    showFilter: false,
    valueGetter: blankValueGetter,
    clickable: true,
    action: TABLE_ACTION.GRID_SELECTION,
    sortable: true,
  },
  {
    name: "Claim Id",
    field: "claim_ids",
    showFilter: false,
    valueGetter: arrayToString,
    clickable: false,
    sortable: false,
  },
  {
    name: "Payment Amount(₹)",
    field: "payout_amounts",
    showFilter: false,
    valueGetter: arrayToString,
    clickable: false,
    sortable: false,
  },
  {
    name: "Area(acres)",
    field: "area",
    showFilter: false,
    valueGetter: valueWithDecimalPlace,
    clickable: false,
    sortable: true,
  },
  {
    name: "Count",
    field: "claims_count",
    showFilter: false,
    valueGetter: blankValueGetter,
    clickable: false,
    sortable: true,
  },
];

export const CLAIMS_AMOUNT_DISBURSED_HEADERS: ColumnHeaderType[] = [
  {
    name: "Grid Id",
    field: "gridid",
    showFilter: false,
    valueGetter: blankValueGetter,
    clickable: true,
    action: TABLE_ACTION.GRID_SELECTION,
    sortable: true,
  },
  {
    name: "Claim Id",
    field: "claim_ids",
    showFilter: false,
    valueGetter: arrayToString,
    clickable: false,
    sortable: false,
  },
  {
    name: "Payment Amount(₹)",
    field: "payout_amounts",
    showFilter: false,
    valueGetter: arrayToString,
    clickable: false,
    sortable: false,
  },
  {
    name: "Area(acres)",
    field: "area",
    showFilter: false,
    valueGetter: valueWithDecimalPlace,
    clickable: false,
    sortable: true,
  },
];

export const CLAIMS_CLAIM_RADIO_HEADERS: ColumnHeaderType[] = [
  {
    name: "Grid Id",
    field: "gridid",
    showFilter: false,
    valueGetter: blankValueGetter,
    clickable: true,
    action: TABLE_ACTION.GRID_SELECTION,
    sortable: true,
  },
  {
    name: "Claim Id",
    field: "claim_ids",
    showFilter: false,
    valueGetter: arrayToString,
    clickable: false,
    sortable: false,
  },
  {
    name: "Payment Amount(₹)",
    field: "payout_amounts",
    showFilter: false,
    valueGetter: arrayToString,
    clickable: false,
    sortable: false,
  },
  {
    name: "Loss Ratio",
    field: "loss_ratio",
    showFilter: false,
    clickable: false,
    sortable: true,
  },
];

export const CLAIMS_CLAIM_COUNT_HEADERS: ColumnHeaderType[] = [
  {
    name: "Grid Id",
    field: "gridid",
    showFilter: false,
    valueGetter: blankValueGetter,
    clickable: true,
    action: TABLE_ACTION.GRID_SELECTION,
    sortable: true,
  },
  {
    name: "Claim Count",
    field: "claims_count",
    showFilter: false,
    valueGetter: blankValueGetter,
    clickable: false,
    sortable: true,
  },
];

export const CLAIMS_PENDING: ColumnHeaderType[] = [
  {
    name: "Grid Id",
    field: "gridid",
    showFilter: false,
    valueGetter: blankValueGetter,
    clickable: true,
    action: TABLE_ACTION.GRID_SELECTION,
    sortable: true,
  },
  {
    name: "Claim Id",
    field: "gridid",
    showFilter: false,
    valueGetter: blankValueGetter,
    clickable: false,
    sortable: true,
  },
];

export const INSURED_AREA_HEADER: ColumnHeaderType[] = [
  {
    name: "Grid Id",
    field: "gridid",
    showFilter: false,
    valueGetter: blankValueGetter,
    clickable: true,
    action: TABLE_ACTION.GRID_SELECTION,
    sortable: true,
  },
  {
    name: "Area(acres)",
    field: "area",
    showFilter: false,
    valueGetter: valueWithDecimalPlace,
    clickable: false,
    sortable: true,
  },
];

export const getCountryStateTableHeader = (
  columnName: string,
): ColumnHeaderType[] => {
  return [
    {
      name: "State",
      field: "sid",
      showFilter: false,
      valueGetter: stateNameGetter,
      clickable: false,
      sortable: true,
    },
    {
      name: columnName,
      field: "total",
      showFilter: false,
      valueGetter: numberFormat,
      clickable: false,
      sortable: true,
    },
  ];
};

export const CLAIMS_COUNTRY_STATE_COUNT_AMOUNT_HEADERS: ColumnHeaderType[] = [
  {
    name: "State",
    field: "vstate",
    showFilter: true,
    valueGetter: blankValueGetter,
    clickable: false,
    sortable: true,
  },
  {
    name: "Count",
    field: "claims_count",
    showFilter: false,
    valueGetter: numberFormat,
    clickable: false,
    sortable: true,
  },
  {
    name: "Amount Paid",
    field: "amount",
    showFilter: false,
    valueGetter: (row: any) => {
      const n = Number(row?.amount);
      if (!Number.isFinite(n)) return "NA";
      return "₹" + n.toLocaleString("en-IN", { minimumFractionDigits: 2 });
    },
    clickable: false,
    sortable: true,
  },
];

export const CLAIMS_COUNTRY_STATE_COUNT_AMOUNT_HEADERS_ABNP: ColumnHeaderType[] =
  [
    {
      name: "State",
      field: "vstate",
      showFilter: true,
      valueGetter: blankValueGetter,
      clickable: false,
      sortable: true,
    },
    {
      name: "Count",
      field: "claims_count",
      showFilter: false,
      valueGetter: numberFormat,
      clickable: false,
      sortable: true,
    },
    {
      name: "Amount To Be Paid",
      field: "amount",
      showFilter: false,
      valueGetter: (row: any) => {
        const n = Number(row?.amount);
        if (!Number.isFinite(n)) return "NA";
        return "₹" + n.toLocaleString("en-IN", { minimumFractionDigits: 2 });
      },
      clickable: false,
      sortable: true,
    },
  ];

export const CLAIMS_STATE_GRID_HEADERS_PAID: ColumnHeaderType[] = [
  {
    name: "Grid Id",
    field: "gridid",
    showFilter: true,
    valueGetter: blankValueGetter,
    sortable: true,
    clickable: true,
    action: TABLE_ACTION.GRID_SELECTION,
  },
  {
    name: "Claim Id",
    field: "claim_ids",
    valueGetter: arrayToString,
    sortable: false,
  },
  {
    name: "Amount Paid",
    field: "amount",
    sortable: true,
    valueGetter: (row: any) => {
      const raw = Number(row?.amount);
      if (!Number.isFinite(raw)) return "₹0";
      return "₹" + raw.toLocaleString("en-IN", { minimumFractionDigits: 2 });
    },
  },
  {
    name: "Area(acres)",
    field: "area",
    valueGetter: valueWithDecimalPlace,
    sortable: true,
  },
  {
    name: "Count",
    field: "claims_count",
    valueGetter: numberFormat,
    sortable: true,
  },
];

export const CLAIMS_STATE_GRID_HEADERS_ABNP: ColumnHeaderType[] = [
  {
    name: "Grid Id",
    field: "gridid",
    showFilter: true,
    valueGetter: blankValueGetter,
    sortable: true,
    clickable: true,
    action: TABLE_ACTION.GRID_SELECTION,
  },
  {
    name: "Claim Id",
    field: "claim_ids",
    valueGetter: arrayToString,
    sortable: false,
  },
  {
    name: "Amount To Be Paid",
    field: "amount",
    sortable: true,
    valueGetter: (row: any) => {
      const raw = Number(row?.amount);
      if (!Number.isFinite(raw)) return "₹0";
      return "₹" + raw.toLocaleString("en-IN", { minimumFractionDigits: 2 });
    },
  },
  {
    name: "Area(acres)",
    field: "area",
    valueGetter: valueWithDecimalPlace,
    sortable: true,
  },
  {
    name: "Count",
    field: "claims_count",
    valueGetter: numberFormat,
    sortable: true,
  },
];

export const CLAIMS_RRC_GRID_HEADERS: ColumnHeaderType[] = [
  {
    name: "Grid Id",
    field: "gridid",
    showFilter: false,
    valueGetter: blankValueGetter,
    clickable: true,
    action: TABLE_ACTION.GRID_SELECTION,
    sortable: true,
  },
  {
    name: "Claim Id",
    field: "claim_ids",
    showFilter: false,
    valueGetter: arrayToString,
    clickable: false,
    sortable: false,
  },
  {
    name: "Payment Amount(₹)",
    field: "payout_amounts",
    showFilter: false,
    valueGetter: arrayToString,
    clickable: false,
    sortable: false,
  },
  {
    name: "Area(acres)",
    field: "area",
    showFilter: false,
    valueGetter: valueWithDecimalPlace,
    clickable: false,
    sortable: true,
  },
  {
    name: "Count",
    field: "claims_count",
    showFilter: false,
    valueGetter: blankValueGetter,
    clickable: false,
    sortable: true,
  },
]; /** ------------------------------------------------------------------
 *  Claims (Country-level) : Rejected | Repudiated | CWP
 *  Table columns: State | Count | Amount
 *  (APPENDED ONCE ONLY)
 * ------------------------------------------------------------------ */

export const CLAIMS_COUNTRY_STATE_COUNT_AMOUNT_HEADERS_PI: ColumnHeaderType[] =
  [
    {
      name: "State",
      field: "state",
      showFilter: true,
      valueGetter: blankValueGetter,
      clickable: false,
      sortable: true,
    },
    {
      name: "Count",
      field: "count",
      showFilter: false,
      valueGetter: numberFormat,
      clickable: false,
      sortable: true,
    },
    {
      name: "Amount To Be Paid",
      field: "amount",
      showFilter: false,
      valueGetter: numberCurrencyFormatInTable,
      clickable: false,
      sortable: true,
    },
  ];

export const CLAIMS_COUNTRY_STATE_COUNT_AMOUNT_HEADERS_CP: ColumnHeaderType[] =
  [
    {
      name: "State",
      field: "vstate",
      showFilter: true,
      valueGetter: blankValueGetter,
      clickable: false,
      sortable: true,
    },
    {
      name: "Count",
      field: "claims_count",
      showFilter: false,
      valueGetter: numberFormat,
      clickable: false,
      sortable: true,
    },
    {
      name: "Amount Paid",
      field: "amount",
      showFilter: false,
      valueGetter: numberCurrencyFormatInTable,
      clickable: false,
      sortable: true,
    },
  ];

export const CLAIMS_COUNTRY_STATE_COUNT_AMOUNT_HEADERS_LS: ColumnHeaderType[] =
  [
    {
      name: "State",
      field: "vstate",
      showFilter: true,
      valueGetter: blankValueGetter,
      clickable: false,
      sortable: true,
    },
    {
      name: "Count",
      field: "claims_count",
      showFilter: false,
      valueGetter: numberFormat,
      clickable: false,
      sortable: true,
    },
    {
      name: "Reserve Amount",
      field: "amount",
      showFilter: false,
      valueGetter: numberCurrencyFormatInTable,
      clickable: false,
      sortable: true,
    },
  ];
// --- utils/TableHeaders.tsx ---

/** ------------------------------------------------------------------
 *  Claims (State-level when a specific state is selected)
 *  District-level aggregation: District | Count | Amount
 *  For ABNP, label -> "Amount To Be Paid", others -> "Amount Paid"
 *  Field key is consistently "amount" to keep the table binding simple.
 * ------------------------------------------------------------------ */

export const CLAIMS_STATE_DISTRICT_COUNT_AMOUNT_HEADERS: ColumnHeaderType[] = [
  {
    name: "District",
    field: "district",
    showFilter: true,
    valueGetter: blankValueGetter,
    clickable: false,
    sortable: true,
  },
  {
    name: "Count",
    field: "count",
    showFilter: false,
    valueGetter: numberFormat,
    clickable: false,
    sortable: true,
  },
  {
    name: "Amount Paid",
    field: "amount",
    showFilter: false,
    valueGetter: numberCurrencyFormatInTable,
    clickable: false,
    sortable: true,
  },
];

export const CLAIMS_STATE_DISTRICT_COUNT_AMOUNT_HEADERS_ABNP: ColumnHeaderType[] =
  [
    {
      name: "District",
      field: "district",
      showFilter: true,
      valueGetter: blankValueGetter,
      clickable: false,
      sortable: true,
    },
    {
      name: "Count",
      field: "count",
      showFilter: false,
      valueGetter: numberFormat,
      clickable: false,
      sortable: true,
    },
    {
      name: "Amount To Be Paid",
      field: "amount",
      showFilter: false,
      valueGetter: numberCurrencyFormatInTable,
      clickable: false,
      sortable: true,
    },
  ];

export const getClaimsStateHeadersByLevel2 = (level2: string) => {
  const l2 = String(level2 || "")
    .toLowerCase()
    .trim();
  switch (l2) {
    case "approved but payment not initiated":
      return CLAIMS_STATE_DISTRICT_COUNT_AMOUNT_HEADERS_ABNP;
    case "payment initiated but not completed":
      return CLAIMS_COUNTRY_STATE_COUNT_AMOUNT_HEADERS_PI;
    case "claim paid":
      return CLAIMS_COUNTRY_STATE_COUNT_AMOUNT_HEADERS_CP;
    case "claim pending (before approval) - loss reserve":
      return CLAIMS_COUNTRY_STATE_COUNT_AMOUNT_HEADERS_LS;
    case "rejected":
    case "repudiated":
    case "cwp":
    default:
      return CLAIMS_STATE_DISTRICT_COUNT_AMOUNT_HEADERS;
  }
};

// -----------------------------------------------------------------------------
// Claims — STATE SELECTED (Grid summary): Grid Id | Claim Id | Amount | Area | Count
// Amount label flips for ABNP
// -----------------------------------------------------------------------------

export const CLAIMS_STATE_GRID_HEADERS_CP: ColumnHeaderType[] = [
  {
    name: "Grid Id",
    field: "gridid",
    showFilter: true,
    valueGetter: blankValueGetter,
    sortable: true,
    clickable: true,
    action: TABLE_ACTION.GRID_SELECTION,
  },
  {
    name: "Claim Id",
    field: "claim_ids",
    valueGetter: arrayToString,
    sortable: false,
  },

  {
    name: "Amount Paid",
    field: "amount",
    sortable: true,
    valueGetter: (row: any) => {
      const raw = Number(row?.amount);
      if (!Number.isFinite(raw)) return "₹0";
      return "₹" + raw.toLocaleString("en-IN", { minimumFractionDigits: 2 });
    },
  },

  {
    name: "Area(acres)",
    field: "area",
    valueGetter: valueWithDecimalPlace,
    sortable: true,
  },
  {
    name: "Count",
    field: "claims_count",
    valueGetter: numberFormat,
    sortable: true,
  },
];

export const CLAIMS_STATE_GRID_HEADERS_LS: ColumnHeaderType[] = [
  {
    name: "Grid Id",
    field: "gridid",
    showFilter: true,
    valueGetter: blankValueGetter,
    sortable: true,
    clickable: true,
    action: TABLE_ACTION.GRID_SELECTION,
  },
  {
    name: "Claim Id",
    field: "claim_ids",
    valueGetter: arrayToString,
    sortable: false,
  },

  {
    name: "Reserve Amount",
    field: "amount",
    showFilter: false,
    // ✅ Compute reserve from array or nested details; default to 0; format as currency
    valueGetter: (row: any) => {
      const reserveFromArray = Array.isArray(row?.loss_reserve_values)
        ? row.loss_reserve_values.reduce(
            (s: number, v: any) => s + (Number(v) || 0),
            0,
          )
        : 0;

      const reserveFromDetails = Array.isArray(row?.claims?.claim_details)
        ? row.claims.claim_details.reduce(
            (s: number, cd: any) => s + (Number(cd?.loss_reserve_amount) || 0),
            0,
          )
        : 0;

      const amount = Number.isFinite(
        Number(reserveFromArray || reserveFromDetails),
      )
        ? Number(reserveFromArray || reserveFromDetails)
        : 0;

      return numberFormatWithCurrency(amount);
    },
    clickable: false,
    sortable: true,
  },

  {
    name: "Area(acres)",
    field: "area",
    valueGetter: valueWithDecimalPlace,
    sortable: true,
  },
  {
    name: "Count",
    field: "claims_count",
    valueGetter: numberFormat,
    sortable: true,
  },
];

export const getClaimsStateGridHeadersByLevel2 = (level2: string) => {
  const l2 = String(level2 || "")
    .toLowerCase()
    .trim();
  return l2 === "claim paid"
    ? CLAIMS_STATE_GRID_HEADERS_CP
    : l2 === "approved but payment not initiated" ||
        l2 === "payment initiated but not completed"
      ? CLAIMS_STATE_GRID_HEADERS_ABNP
      : l2 === "claim pending (before approval) - loss reserve"
        ? CLAIMS_STATE_GRID_HEADERS_LS
        : CLAIMS_STATE_GRID_HEADERS_PAID;
};

export const CLAIMS_REJECTED_COUNTRY_HEADERS =
  CLAIMS_COUNTRY_STATE_COUNT_AMOUNT_HEADERS;
export const CLAIMS_REPUDIATED_COUNTRY_HEADERS =
  CLAIMS_COUNTRY_STATE_COUNT_AMOUNT_HEADERS;
export const CLAIMS_CWP_COUNTRY_HEADERS =
  CLAIMS_COUNTRY_STATE_COUNT_AMOUNT_HEADERS;
export const CLAIMS_APPROVED_BUT_PAYMENT_NOT_INITIATED_COUNTRY_HEADERS =
  CLAIMS_COUNTRY_STATE_COUNT_AMOUNT_HEADERS_ABNP;
export const CLAIMS_PAYMENT_INITIATED_BUT_NOT_COMPLETED_COUNTRY_HEADERS =
  CLAIMS_COUNTRY_STATE_COUNT_AMOUNT_HEADERS_PI;
export const CLAIMS_CLAIMS_PAID_COUNTRY_HEADERS =
  CLAIMS_COUNTRY_STATE_COUNT_AMOUNT_HEADERS_CP;
export const CLAIMS_LOSS_RESERVE_COUNTRY_HEADERS =
  CLAIMS_COUNTRY_STATE_COUNT_AMOUNT_HEADERS_LS;

export const getClaimsCountryHeadersByLevel2 = (level2: string) => {
  const l2 = String(level2 || "").toLowerCase();
  switch (l2) {
    case "rejected":
    case "repudiated":

    case "cwp":
      return CLAIMS_COUNTRY_STATE_COUNT_AMOUNT_HEADERS;
    case "approved but payment not initiated":
      return CLAIMS_COUNTRY_STATE_COUNT_AMOUNT_HEADERS_ABNP;
    case "payment initiated but not completed":
      return CLAIMS_COUNTRY_STATE_COUNT_AMOUNT_HEADERS_PI;
    case "claim paid":
      return CLAIMS_COUNTRY_STATE_COUNT_AMOUNT_HEADERS_CP;
    case "claim pending (before approval) - loss reserve":
      return CLAIMS_COUNTRY_STATE_COUNT_AMOUNT_HEADERS_LS;
    default:
      return CLAIMS_COUNTRY_STATE_COUNT_AMOUNT_HEADERS;
  }
};
